import { useCasesStore } from "../store/hooks";
import { useCallback } from "react";

import { ICases } from "../interfaces/admin/cases/cases";
import { useApiCall } from "./useApiCall";
import { EnAreaNames, EnFormNames } from "../store/caseSettingsStore";

export const useCasesApi = () => {
  const { api } = useApiCall();
  const casesStore = useCasesStore();

  const getCases = useCallback(
    async (isLoading: boolean = true) => {
      const res: ICases = await api.apiInstance.get(`situations`, isLoading);
      casesStore.setCases(res.items, res.forms);
      return !!res;
    },
    [api.apiInstance, casesStore]
  );

  const acceptCase = useCallback(
    async ({
      area,
      form,
      caseId,
      isPublic,
    }: {
      area: EnAreaNames;
      form: EnFormNames;
      caseId: string;
      isPublic: boolean;
    }) => {
      const res: ICases = await api.apiInstance.put(
        `situation-flow/${area}/${form}/${caseId}/accept`,
        {
          isPublic,
        }
      );
      await getCases(true);
      return !!res;
    },
    [api.apiInstance, getCases]
  );

  const editStatusCase = useCallback(
    async ({
      id,
      area,
      form,
      isPublic,
    }: {
      area: EnAreaNames;
      form: EnFormNames;
      id: number;
      isPublic: boolean;
    }) => {
      const res: ICases = await api.apiInstance.put(
        `situation-flow/${area}/${form}/${id}/is-public`,
        { isPublic }
      );
      await getCases(true);
      return !!res;
    },
    [api.apiInstance, getCases]
  );

  const rejectCase = useCallback(
    async ({
      area,
      form,
      caseId,
      comment,
    }: {
      area: EnAreaNames;
      form: EnFormNames;
      caseId: string;
      comment: string;
    }) => {
      const res: ICases = await api.apiInstance.put(
        `situation-flow/${area}/${form}/${caseId}/reject`,
        {
          comment,
        }
      );
      if (res) {
        await getCases(true);
      }
      return !!res;
    },
    [api.apiInstance, getCases]
  );

  const getListOfMyCases = useCallback(
    async (isLoading: boolean = true) => {
      const res = await api.apiInstance.get("situations/my", isLoading);
      if (res) {
        casesStore.setListOfMyCases(res.items, res.forms);
      }
    },
    [api.apiInstance, casesStore]
  );

  const getListOfOtherCases = useCallback(
    async (isLoading: boolean = true, limit?: number) => {
      const res = await api.apiInstance.get(
        limit ? `situations/public?limit=${limit}` : "situations/public",
        isLoading
      );
      if (res) {
        if (limit) {
          casesStore.setListOfLast3Cases(res.items);
        } else {
          casesStore.setListOfOtherCases(res.items, res.forms);
        }
      }
    },
    [api.apiInstance, casesStore]
  );

  return {
    getCases,
    acceptCase,
    editStatusCase,
    rejectCase,
    getListOfMyCases,
    getListOfOtherCases,
  };
};
